import React from "react"
import useRefCallback from "@myvp/shared/src/hooks/use-ref-callback"
import { breakpointSizes } from "@myvp/shared/src/styles/breakpoints"

const useMediaQuery = ({
  size,
  callback = () => {},
  dimension = "width",
}: {
  size: keyof typeof breakpointSizes
  callback?: (matches: boolean) => void
  dimension?: "width" | "height"
}) => {
  const callbackRef = useRefCallback(callback)
  const queries = React.useMemo(
    () => ({
      xxs: `(max-${dimension}: ${breakpointSizes.xxs}px)`,
      xs: `(max-${dimension}: ${breakpointSizes.xs}px)`,
      sm: `(max-${dimension}: ${breakpointSizes.sm}px)`,
      md: `(max-${dimension}: ${breakpointSizes.md}px)`,
      lg: `(max-${dimension}: ${breakpointSizes.lg}px)`,
    }),
    [dimension]
  )

  const [matches, setMatches] = React.useState(
    () => window.matchMedia(queries[size]).matches
  )

  React.useEffect(() => {
    let mounted = true

    const mql = window.matchMedia(queries[size])
    const onChange = () => {
      if (!mounted) {
        return
      }
      callbackRef.current(!!mql.matches)
      setMatches(!!mql.matches)
    }

    mql.addEventListener("change", onChange)
    setMatches(mql.matches)

    return () => {
      mounted = false
      mql.removeEventListener("change", onChange)
    }
  }, [queries, size])

  return matches
}

export default useMediaQuery
